import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ActionTypes, IPrompt, ISource } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

import { PromptView } from './Message/PromptView';
import { DateSeparator } from './DateSeparator';
import './PromptList.scss';

export interface PromptListProps {
    prompts: IPrompt[];
    conversationTitle?: string;
    onUpdate: (actionType: ActionTypes, prompts: IPrompt[], updatedPromptIndex: number) => void;
    onUpdateAnsweredQuery: (updatedPromptIndex: number, newQuery: string) => void;
    onSourceClick: (source: ISource) => void;
    onEditingChange?: (isEditing: boolean) => void;
}

// space kept between the top of the viewport and the top of the anchored prompt
const ANCHOR_TOP_GAP = 16;
// how long the content height must stay unchanged before the initial scroll to bottom is considered done
const BOTTOM_SETTLE_MS = 300;
// upper bound for that pinning, so a never settling conversation cannot keep the loop alive
const BOTTOM_PIN_MAX_MS = 3000;

// renders the list of messages
export const PromptList: FC<PromptListProps> = (props: PromptListProps) => {
    const { prompts, onUpdate, onUpdateAnsweredQuery, onSourceClick, onEditingChange } = props;
    const promptList = useRef<HTMLDivElement | null>(null);
    // sentinel at the end of the list to reliably scroll into view
    const endRef = useRef<HTMLDivElement | null>(null);
    // wrapper of the last prompt, used to anchor the newest answer to the top of the viewport
    const lastPromptRef = useRef<HTMLDivElement | null>(null);
    // filler that guarantees the newest prompt can be scrolled up to the top of the viewport
    const spacerRef = useRef<HTMLDivElement | null>(null);
    // hide list until we've scrolled into position to avoid visible "jump" effect
    const [isReady, setIsReady] = useState<boolean>(false);

    // true once the initial (conversation load) positioning has been done
    const didInitialScroll = useRef<boolean>(false);
    // timestamp until which the initial scroll to bottom may still be re-applied
    const settleUntil = useRef<number>(0);
    const previousCount = useRef<number>(0);
    // while true, the newest prompt is kept at the top of the viewport
    const anchorToTop = useRef<boolean>(false);
    // set as soon as the user takes control of the scroll position
    const userScrolled = useRef<boolean>(false);
    const lastProgrammaticTop = useRef<number>(0);

    // Utility: find the nearest scrollable ancestor of an element
    const getScrollParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;
        let parent: HTMLElement | null = element.parentElement;
        const overflowRegex = /(auto|scroll)/;

        while (parent) {
            const style = window.getComputedStyle(parent);
            const overflowY = style.overflowY;
            if (overflowRegex.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
            parent = parent.parentElement;
        }
        // fallback to document.scrollingElement
        return document.scrollingElement as HTMLElement | null;
    }, []);

    // the list itself is the scroll container, but fall back to an ancestor if the styling ever changes
    const getScrollContainer = useCallback((): HTMLElement | null => {
        const element = promptList.current;
        if (!element) return null;
        try {
            if (/(auto|scroll)/.test(window.getComputedStyle(element).overflowY)) return element;
        } catch {}
        return getScrollParent(element);
    }, [getScrollParent]);

    // the spacer only has to make up for what the newest prompt is missing to fill the viewport
    const updateSpacer = useCallback(() => {
        const container = getScrollContainer();
        const target = lastPromptRef.current;
        if (!container || !target || !spacerRef.current) return;
        const missing = container.clientHeight - target.getBoundingClientRect().height - ANCHOR_TOP_GAP;
        spacerRef.current.style.height = `${Math.max(0, Math.round(missing))}px`;
    }, [getScrollContainer]);

    // keeps the newest prompt at the top of the viewport
    const anchorLastPromptToTop = useCallback(() => {
        const container = getScrollContainer();
        const target = lastPromptRef.current;
        if (!container || !target) return;

        updateSpacer();
        const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
        const desired = Math.max(0, container.scrollTop + offset - ANCHOR_TOP_GAP);
        lastProgrammaticTop.current = desired;
        try { container.scrollTop = desired; } catch {}
    }, [getScrollContainer, updateSpacer]);

    const scrollToBottom = useCallback(() => {
        const container = getScrollContainer();
        if (container) {
            try { container.scrollTop = container.scrollHeight; return; } catch {}
        }
        try { endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' }); } catch {}
    }, [getScrollContainer]);

    // messages keep growing after mount (markdown, code blocks, images), so follow the bottom until the height settles
    const pinToBottomUntilSettled = useCallback((): (() => void) => {
        let raf = 0;
        let cancelled = false;
        let lastHeight = -1;
        const start = performance.now();
        let stableSince = start;

        const step = () => {
            if (cancelled || userScrolled.current) return;
            const container = getScrollContainer();
            const now = performance.now();
            const height = container ? container.scrollHeight : -1;
            if (height !== lastHeight) {
                lastHeight = height;
                stableSince = now;
                scrollToBottom();
            }
            if (now - stableSince < BOTTOM_SETTLE_MS && now - start < BOTTOM_PIN_MAX_MS) {
                raf = requestAnimationFrame(step);
            }
        };
        step();

        return () => { cancelled = true; cancelAnimationFrame(raf); };
    }, [getScrollContainer, scrollToBottom]);

    // detect that the user took over the scroll position, so we stop moving it for them
    useLayoutEffect(() => {
        const container = getScrollContainer();
        if (!container) return;

        const releaseAnchor = () => { userScrolled.current = true; };
        const onScroll = () => {
            if (!anchorToTop.current || userScrolled.current) return;
            const maxTop = container.scrollHeight - container.clientHeight;
            // ignore our own scrolls and the browser clamping scrollTop when content shrinks
            if (Math.abs(container.scrollTop - lastProgrammaticTop.current) <= 2) return;
            if (container.scrollTop >= maxTop - 2 && lastProgrammaticTop.current > container.scrollTop) return;
            userScrolled.current = true;
        };
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as Node | null;
            // only keys pressed inside the list can scroll it
            if (!target || !container.contains(target)) return;
            const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];
            if (keys.includes(event.key)) releaseAnchor();
        };

        // the spacer depends on the viewport height (window resize, device rotation, mobile keyboard)
        let containerRo: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            try {
                containerRo = new ResizeObserver(() => { if (anchorToTop.current) updateSpacer(); });
                containerRo.observe(container);
            } catch {}
        }

        container.addEventListener('wheel', releaseAnchor, { passive: true });
        container.addEventListener('touchmove', releaseAnchor, { passive: true });
        container.addEventListener('pointerdown', releaseAnchor);
        container.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('keydown', onKeyDown);

        return () => {
            container.removeEventListener('wheel', releaseAnchor);
            container.removeEventListener('touchmove', releaseAnchor);
            container.removeEventListener('pointerdown', releaseAnchor);
            container.removeEventListener('scroll', onScroll);
            window.removeEventListener('keydown', onKeyDown);
            if (containerRo) try { containerRo.disconnect(); } catch {}
        };
    }, [getScrollContainer, updateSpacer]);

    useLayoutEffect(() => {
        let mounted = true;

        const count = prompts.length;
        const isNewPrompt = didInitialScroll.current && count > previousCount.current;
        previousCount.current = count;
        // the list mounts right after the first query of a conversation is sent: that prompt is local (no id) and still unanswered
        const lastPrompt = count > 0 ? prompts[count - 1] : undefined;
        const awaitingFirstAnswer = !didInitialScroll.current && !!lastPrompt && !lastPrompt.answer && !lastPrompt.id;

        // first render of an existing conversation keeps the historical behaviour: show the latest messages
        if (!didInitialScroll.current && !awaitingFirstAnswer) {
            didInitialScroll.current = true;
            setIsReady(false);
            settleUntil.current = performance.now() + BOTTOM_PIN_MAX_MS;

            const cancelPin = pinToBottomUntilSettled();
            // reveal quickly, the pinning above keeps following any later growth
            const showT = window.setTimeout(() => { if (mounted) setIsReady(true); }, 150);

            return () => {
                mounted = false;
                cancelPin();
                clearTimeout(showT);
            };
        }

        // past the initial positioning the list must stay visible, whatever updates the prompts
        setIsReady(true);

        // a prompt was just submitted: start reading the new answer from its first line
        if (isNewPrompt || awaitingFirstAnswer) {
            didInitialScroll.current = true;
            anchorToTop.current = true;
            userScrolled.current = false;
            settleUntil.current = 0;
        }

        if (!anchorToTop.current) {
            // an unrelated store update re-rendered the list while the initial scroll to bottom was still settling
            if (userScrolled.current || performance.now() >= settleUntil.current) return;
            return pinToBottomUntilSettled();
        }

        // the spacer stays in sync even when the user took over, so no dead space is left below a long answer
        const reposition = () => {
            if (!mounted) return;
            if (userScrolled.current) updateSpacer();
            else anchorLastPromptToTop();
        };

        reposition();
        // re-anchor while the answer is being produced and while late content (markdown, images) changes its height
        const t = window.setTimeout(reposition, 100);

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && lastPromptRef.current) {
            try {
                ro = new ResizeObserver(reposition);
                ro.observe(lastPromptRef.current);
            } catch {}
        }

        return () => {
            mounted = false;
            clearTimeout(t);
            if (ro) try { ro.disconnect(); } catch {}
        };
    }, [prompts, getScrollContainer, updateSpacer, anchorLastPromptToTop, pinToBottomUntilSettled]);

    // Helper function to check if two dates are on different days
    const isSameDay = (date1: Date, date2: Date): boolean => {
        return date1.getDate() === date2.getDate() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getFullYear() === date2.getFullYear();
    };

    // Helper function to check if we need to show a date separator
    const needsDateSeparator = (currentIndex: number): boolean => {
        if (currentIndex === 0) return true; // Always show date separator for the first prompt
        
        const currentPrompt = prompts[currentIndex];
        const previousPrompt = prompts[currentIndex - 1];
        
        if (!currentPrompt.queryOn || !previousPrompt.queryOn) return false;
        
        return !isSameDay(new Date(currentPrompt.queryOn), new Date(previousPrompt.queryOn));
    };

    return (
        <div className='promptList' ref={promptList} style={{ opacity: isReady ? 1 : 0, transition: 'opacity 160ms ease' }}>
            {prompts.map((prompt, index) => (
                <div key={`prompt_container_${prompt.id}`} ref={index === prompts.length - 1 ? lastPromptRef : undefined}>
                    {needsDateSeparator(index) && prompt.queryOn && (
                        <DateSeparator 
                            key={`date_separator_${index}`}
                            date={new Date(prompt.queryOn)} 
                        />
                    )}
                    <PromptView
                        key={`prompt_${prompt.id}`}
                        prompt={prompt}
                        isLastPrompt={index === prompts.length - 1}
                        onEditQuery={(query) => {
                            onUpdateAnsweredQuery(index, query);
                        }}
                        onUpdate={(actionType, newPrompt) => {
                            const newPrompts = [...prompts];
                            newPrompts[index] = newPrompt;
                            onUpdate(actionType, newPrompts, index);
                        }}
                        onSourceClick={onSourceClick}
                        onEditingChange={onEditingChange}
                    />
                </div>
            ))}
            {/* grows so the newest prompt can reach the top of the viewport, shrinks as the answer gets longer */}
            <div ref={spacerRef} aria-hidden style={{ height: 0, width: '100%', flexShrink: 0 }} />
            {/* sentinel element used to scroll to bottom */}
            <div ref={endRef} aria-hidden style={{ height: 1, width: '100%' }} />
        </div>
    );
};
